import validator from 'validator'
import { parsePhoneNumber as libParsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import { ValidationResult, PhoneData } from '../types'

// Common validation functions
export const required = (value: any): ValidationResult => {
  const isValid = Boolean(value !== null && value !== undefined && value.toString().trim() !== '')
  return {
    isValid,
    error: isValid ? undefined : 'This field is required'
  }
}

export const minLength = (min: number) => (value: string): ValidationResult => {
  const isValid = Boolean(value && value.length >= min)
  return {
    isValid,
    error: isValid ? undefined : `Must be at least ${min} characters`
  }
}

export const maxLength = (max: number) => (value: string): ValidationResult => {
  const isValid = Boolean(!value || value.length <= max)
  return {
    isValid,
    error: isValid ? undefined : `Must be at most ${max} characters`
  }
}

// Email validation using validator.js library
export const email = (value: string): ValidationResult => {
  const isValid = !value || validator.isEmail(value)
  return {
    isValid,
    error: isValid ? undefined : 'Please enter a valid email address'
  }
}

// Phone validation using libphonenumber-js library
export const parsePhoneNumber = (phoneNumber: string, defaultCountry: string = 'CH'): PhoneData => {
  // Handle empty input
  if (!phoneNumber || phoneNumber.trim() === '') {
    return {
      countryCode: '',
      nationalNumber: '',
      formattedNumber: '',
      isValid: false
    }
  }

  try {
    const parsed = libParsePhoneNumber(phoneNumber, defaultCountry as any)

    if (!parsed) {
      return {
        countryCode: '',
        nationalNumber: phoneNumber,
        formattedNumber: phoneNumber,
        isValid: false
      }
    }

    return {
      countryCode: `+${parsed.countryCallingCode}`,
      nationalNumber: parsed.nationalNumber,
      formattedNumber: parsed.formatInternational(),
      isValid: parsed.isValid()
    }
  } catch (error) {
    return {
      countryCode: '',
      nationalNumber: phoneNumber,
      formattedNumber: phoneNumber,
      isValid: false
    }
  }
}

export const validatePhoneNumber = (phoneNumber: string, defaultCountry: string = 'CH'): ValidationResult => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { isValid: false, error: 'Phone number is required' }
  }

  try {
    const isValid = isValidPhoneNumber(phoneNumber, defaultCountry as any)
    return {
      isValid,
      error: isValid ? undefined : 'Please enter a valid phone number'
    }
  } catch (error) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number'
    }
  }
}

// Address validation functions
export const validateAddress = (address: Partial<import('../types').AddressData>, required: boolean = false): Record<string, string> => {
  const errors: Record<string, string> = {}

  // Check if any address field is provided (partial address logic)
  const hasAnyAddressField = Boolean(
    address.streetLine1 || address.streetLine2 || address.city ||
    address.state || address.postalCode || address.country
  )

  // If required=true or any field is provided, validate basic required fields
  const shouldValidateBasicFields = required || hasAnyAddressField

  if (shouldValidateBasicFields || address.streetLine1) {
    if (!address.streetLine1?.trim()) {
      errors.streetLine1 = 'Street address is required'
    }
  }

  if (shouldValidateBasicFields || address.city) {
    if (!address.city?.trim()) {
      errors.city = 'City is required'
    }
  }

  if (shouldValidateBasicFields || address.state) {
    if (!address.state?.trim()) {
      errors.state = 'State/Province/Canton is required'
    }
  }

  if (shouldValidateBasicFields || address.postalCode) {
    if (!address.postalCode?.trim()) {
      errors.postalCode = 'Postal code is required'
    } else {
      // Basic postal code validation
      const postalCodePattern = /^[\w\s-]{3,10}$/
      if (!postalCodePattern.test(address.postalCode)) {
        errors.postalCode = 'Please enter a valid postal code'
      }
    }
  }

  if (shouldValidateBasicFields || address.country) {
    if (!address.country?.trim()) {
      errors.country = 'Country is required'
    }
  }

  return errors
}